import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { alpha, Box, IconButton, Typography } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { DateTime } from "luxon";
import { type FC, useMemo, useState } from "react";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import type { RecurringCandidateRow } from "../../../../services/backend.service";
import { ALPHA, FONT_SIZE, INK, NEUTRAL, RADIUS, SHIFT_NIGHT_FALLBACK, STATUS_BADGE, SURFACE } from "../../../../theme/tokens";
import { useRecurringEmployeeHistory } from "../../hooks";
import { generatedShiftDays, shiftTimeLabels } from "./generatedShiftDays";
import { formatClock, formatIsoDate, NOT_SET } from "./recurringDisplay";

type Props = {
	row: RecurringCandidateRow | null;
	onClose: () => void;
	departmentId: string | undefined;
	/** The window the proposal was calculated over, so the history shown matches what was fitted. */
	windowMonths: number;
};

const DAYS_PER_WEEK = 7;
/** Sunday-first, matching the staffing calendar the reviewer already reads elsewhere in the app. */
const WEEKDAY_HEADS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
/** Enough rows for any month, including one starting on Saturday. */
const CALENDAR_CELLS = 6 * DAYS_PER_WEEK;
const CELL_MIN_HEIGHT = 74;

/**
 * One colour per source, because the question this calendar answers is where they DISAGREE.
 *
 * Green is deliberately not here. It previously inked the proposal AND washed the cell behind it, so
 * the strongest signal on the screen was "this day has something on it" — which every coloured day
 * has — while the distinction that matters, WHICH source, was carried by the weakest contrast.
 *
 * Violet, slate and orange instead: far apart in hue, and far apart in lightness too, so they stay
 * separable in greyscale and for the red-green colour blindness that made a green/amber pair the
 * worst possible choice. Each line also carries an ICON, so the meaning never rests on colour alone.
 */
const LAYERS = {
	proposed: {
		label: "Proposed",
		hint: "what this pattern will create",
		ink: SHIFT_NIGHT_FALLBACK,
		Icon: AutoAwesomeOutlinedIcon,
	},
	scheduled: {
		label: "Scheduled",
		hint: "the shift on the loaded schedule",
		ink: INK.SECONDARY,
		Icon: EventOutlinedIcon,
	},
	actual: {
		label: "Actual",
		hint: "when they really punched in and out",
		ink: STATUS_BADGE.AMBER_INK,
		Icon: CheckCircleOutlineIcon,
	},
} as const;

/** Tint for a day the pattern proposes — the same violet, at wash strength. */
const PROPOSED_WASH = alpha(SHIFT_NIGHT_FALLBACK, ALPHA.BRAND_TINT);
const LINE_ICON_SIZE = 12;

const cellSx = (isShift: boolean, inMonth: boolean) => ({
	minHeight: CELL_MIN_HEIGHT,
	p: 0.75,
	display: "flex",
	flexDirection: "column",
	gap: 0.5,
	border: `1px solid ${isShift ? alpha(SHIFT_NIGHT_FALLBACK, 0.35) : NEUTRAL[200]}`,
	borderRadius: `${RADIUS.CARD}px`,
	backgroundColor: isShift ? PROPOSED_WASH : SURFACE,
	// A day outside the month is context, not content — it stays legible but recedes.
	opacity: inMonth ? 1 : 0.45,
});

/** One time on one day, inked AND iconed by which source it came from. */
const DayLine: FC<{ label: string; layer: keyof typeof LAYERS }> = ({ label, layer }) => {
	const { ink, Icon, label: name } = LAYERS[layer];
	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
			<Icon aria-hidden sx={{ fontSize: LINE_ICON_SIZE, color: ink, flexShrink: 0 }} />
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: ink, lineHeight: 1.3 }}>
				{/* Named for assistive tech, where the icon and the ink both say nothing. */}
				<Box component="span" sx={visuallyHidden}>{`${name}: `}</Box>
				{label}
			</Typography>
		</Box>
	);
};

/**
 * What each colour means, and in one phrase what each SOURCE is.
 *
 * The label alone ("Scheduled") assumes the reader already knows this product's vocabulary. The
 * phrase after it is what makes the calendar readable by someone meeting these three ideas for the
 * first time, which on an onboarding tool is most of its audience.
 */
const CalendarLegend: FC = () => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }} data-testid="recurring-calendar-legend">
		{(Object.keys(LAYERS) as Array<keyof typeof LAYERS>).map((layer) => {
			const { ink, Icon, label, hint } = LAYERS[layer];
			return (
				<Box key={layer} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<Icon aria-hidden sx={{ fontSize: LINE_ICON_SIZE + 2, color: ink, flexShrink: 0 }} />
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: ink }}>{label}</Typography>
					<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>— {hint}</Typography>
				</Box>
			);
		})}
	</Box>
);

/**
 * What one approved pattern would actually put on the calendar, against what really happened.
 *
 * The table says "Every 2 weeks · W1 Fri, Thu, Tue · W2 Mon", which is precise and still hard to
 * picture. This is the same proposal drawn as the month the reviewer is used to reading, so "is this
 * person's schedule right" becomes a question they can answer by looking rather than by decoding.
 *
 * Preview only — the days come from `generatedShiftDays`, which mirrors the backend's generation but
 * does not drive it. Approving still sends candidate ids and the backend regenerates the real rows.
 */
export const RecurringShiftCalendarDialog: FC<Props> = ({ row, onClose, departmentId, windowMonths }) => {
	const [monthOffset, setMonthOffset] = useState(0);
	const { data: history, isLoading: historyLoading } = useRecurringEmployeeHistory(departmentId, row?.nurseId, windowMonths);

	/** `yyyy-MM-dd` -> the times each source recorded that day. */
	const historyByDay = useMemo(() => {
		const map = new Map<string, { scheduled: string[]; actual: string[] }>();
		for (const entry of history?.days ?? []) {
			const iso = typeof entry.day === "string" ? entry.day : (entry.day.toISODate() ?? "");
			map.set(iso, {
				scheduled: entry.scheduled.map((slot) => `${formatClock(slot.start)}-${formatClock(slot.end)}`),
				actual: entry.actual.map((slot) => `${formatClock(slot.start)}-${formatClock(slot.end)}`),
			});
		}
		return map;
	}, [history]);

	// The month to open on: the pattern's own anchor when it has one, else today. An anchored pattern
	// that starts three months out would otherwise open on an empty month and read as "no shifts".
	const baseMonth = useMemo(() => {
		const anchor = row?.anchor ? (typeof row.anchor === "string" ? DateTime.fromISO(row.anchor) : row.anchor) : null;
		const from = anchor?.isValid ? anchor : DateTime.now();
		return from.startOf("month");
	}, [row]);

	const month = baseMonth.plus({ months: monthOffset });
	const gridStart = month.startOf("month").startOf("week").minus({ days: 1 }); // Luxon weeks start Monday; shift to Sunday.
	const gridEnd = gridStart.plus({ days: CALENDAR_CELLS - 1 });

	const shiftDays = useMemo(() => (row ? generatedShiftDays(row, gridStart, gridEnd) : new Set<string>()), [row, gridStart, gridEnd]);
	const times = row ? shiftTimeLabels(row) : [];
	const inMonthCount = useMemo(() => [...shiftDays].filter((iso) => DateTime.fromISO(iso).hasSame(month, "month")).length, [shiftDays, month]);

	const cells = useMemo(() => Array.from({ length: CALENDAR_CELLS }, (_, index) => gridStart.plus({ days: index })), [gridStart]);

	const close = () => {
		setMonthOffset(0);
		onClose();
	};

	return (
		<SystemDialog
			open={row !== null}
			onClose={close}
			onConfirm={close}
			title={row ? `${row.nurseName} — ${row.patternLabel ?? NOT_SET}` : ""}
			maxWidth="lg"
			fullWidth
			hideConfirm
			cancel="Close"
			dataTestId="recurring-shift-calendar-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<IconButton size="small" onClick={() => setMonthOffset((offset) => offset - 1)} aria-label="Previous month">
								<ChevronLeftIcon fontSize="small" />
							</IconButton>
							<Typography sx={{ fontWeight: 600, minWidth: 150, textAlign: "center" }} data-testid="recurring-calendar-month">
								{month.toFormat("MMMM yyyy")}
							</Typography>
							<IconButton size="small" onClick={() => setMonthOffset((offset) => offset + 1)} aria-label="Next month">
								<ChevronRightIcon fontSize="small" />
							</IconButton>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
							<MetaChip label={`${inMonthCount} proposed day${inMonthCount === 1 ? "" : "s"} this month`} accentColor={SHIFT_NIGHT_FALLBACK} />
							{row?.jobName && <MetaChip label={row.jobName} tone="neutral" />}
							{row?.location && <MetaChip label={row.location} tone="info" />}
						</Box>
					</Box>

					<CalendarLegend />
					{historyLoading && (
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }} data-testid="recurring-calendar-history-loading">
							Loading this employee's schedule and punches…
						</Typography>
					)}

					<Box sx={{ display: "grid", gridTemplateColumns: `repeat(${DAYS_PER_WEEK}, minmax(0, 1fr))`, gap: 0.5 }}>
						{WEEKDAY_HEADS.map((weekday) => (
							<Typography key={weekday} sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.MUTED, textAlign: "center", pb: 0.5 }}>
								{weekday}
							</Typography>
						))}
						{cells.map((day) => {
							const iso = day.toISODate() ?? "";
							const isShift = shiftDays.has(iso);
							const real = historyByDay.get(iso);
							const empty = !isShift && !real?.scheduled.length && !real?.actual.length;
							return (
								<Box key={iso} sx={cellSx(isShift, day.hasSame(month, "month"))} data-testid={isShift ? `recurring-calendar-shift-${iso}` : undefined}>
									<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{day.toFormat("MM/dd")}</Typography>
									{isShift && times.map((label) => <DayLine key={`p-${label}`} label={label} layer="proposed" />)}
									{real?.scheduled.map((label) => (
										<DayLine key={`s-${label}`} label={label} layer="scheduled" />
									))}
									{real?.actual.map((label) => (
										<DayLine key={`a-${label}`} label={label} layer="actual" />
									))}
									{empty && <Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.DISABLED }}>--</Typography>}
								</Box>
							);
						})}
					</Box>

					{/* How to READ the calendar, not just what the colours are. The legend names the three
					    sources; this says what their agreement and disagreement each mean, which is the
					    actual skill being asked of the reviewer. */}
					<InlineHint tone="info" contentComponent="div" dataTestId="recurring-calendar-preview-note">
						<Box component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
							How to read this
						</Box>
						<Box component="ul" sx={{ m: 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
							<li>
								A tinted day is one the pattern would create
								{row?.anchor ? <>, starting {formatIsoDate(row.anchor)}</> : null}. All three lines can appear on the same day.
							</li>
							<li>
								<strong>Proposed with nothing under it</strong> — the pattern invents a shift on a day they neither were scheduled for nor worked.
							</li>
							<li>
								<strong>Scheduled or Actual with no Proposed</strong> — a day they really work that this pattern would miss.
							</li>
							<li>
								<strong>Different times on the same day</strong> — the pattern has the right day but the wrong shift.
							</li>
						</Box>
						<Box component="div" sx={{ mt: 0.75 }}>
							Times are exactly as recorded, not rounded to the job's hours. Nothing is written until you approve, and approving regenerates these days on the
							server rather than sending what you see here.
						</Box>
					</InlineHint>
				</Box>
			}
		/>
	);
};
